export type SystemPrintOutcome = 'printed' | 'shared-print-service' | 'shared-fallback';

function waitForPaint(delayMs = 120): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

/**
 * Download HTML as a file (PDF-ish fallback when browser print-to-PDF is unavailable).
 */
export function downloadHtml(html: string, filename: string) {
  const safeName = String(filename || 'print.html').replace(/[^\w.\-()\u4e00-\u9fff]+/g, '_');
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = safeName.endsWith('.html') ? safeName : `${safeName}.html`;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2_000);
}

/**
 * Open the browser print UI from HTML.
 * Prefer iframe + window.print(); fall back to a new tab if needed.
 */
export async function presentSystemPrintFromHtml(params: {
  html: string;
  shareFallbackDialogTitle?: string;
  hidePreview?: () => void;
  isAborted?: () => boolean;
}): Promise<SystemPrintOutcome> {
  if (params.isAborted?.()) {
    return 'printed';
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  document.body.appendChild(iframe);

  const cleanup = () => {
    try {
      iframe.remove();
    } catch {
      // ignore
    }
  };

  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      throw new Error('无法创建打印预览文档');
    }

    doc.open();
    doc.write(params.html);
    doc.close();

    await waitForPaint();
    if (params.isAborted?.()) {
      cleanup();
      return 'printed';
    }

    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      throw new Error('无法打开系统打印面板');
    }

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      printWindow.addEventListener('afterprint', finish, { once: true });
      try {
        printWindow.focus();
        printWindow.print();
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
        return;
      }
      // Some browsers never fire afterprint for hidden iframes.
      window.setTimeout(finish, 1_500);
    });

    cleanup();
    return 'printed';
  } catch (error) {
    cleanup();

    // Fallback: open a dedicated window and print from there.
    try {
      const popup = window.open('', '_blank');
      if (!popup) {
        throw error instanceof Error ? error : new Error(String(error));
      }
      popup.document.open();
      popup.document.write(params.html);
      popup.document.close();
      await waitForPaint(200);
      if (params.isAborted?.()) {
        popup.close();
        return 'printed';
      }
      popup.focus();
      popup.print();
      return 'printed';
    } catch (fallbackError) {
      throw fallbackError instanceof Error
        ? fallbackError
        : new Error(String(fallbackError || error));
    }
  }
}

export function requestClosePrintPreview(params: {
  busy: boolean;
  onClose: () => void;
  title: string;
  message: string;
  stayLabel: string;
  exitLabel: string;
}) {
  if (!params.busy) {
    params.onClose();
    return;
  }

  const confirmed = window.confirm(`${params.title}\n\n${params.message}`);
  if (confirmed) {
    params.onClose();
  }
}
